import { useCallback, useEffect, useMemo, useState } from "react"
import {
  useGetPriceFormDataQuery,
  useLazyGetPriceResortsQuery,
  useSavePriceMutation,
  useDeletePricePhotoMutation,
} from "../../../app/services/transfer/prices"
const SCALAR_FIELDS = [
  "name",
  "state_id",
  "company_id",
  "terminal_id",
  "transport_type_id",
  "from_date",
  "to_date",
  "transfer_type",
  "valute_id",
  "adult_sum",
  "adult_sum_netto",
  "child_sum",
  "child_sum_netto",
  "infant_sum",
  "infant_sum_netto",
  "adult_sum_transfer",
  "adult_sum_transfer_netto",
  "child_sum_transfer",
  "child_sum_transfer_netto",
  "infant_sum_transfer",
  "infant_sum_transfer_netto",
  "child_age_max",
  "infant_age_max",
  "passenger_from",
  "passenger_to",
  "non_refundable_days",
  "on_request_days",
  "important_info",
  "direction",
]

export const SINGLE_PRICE_TYPES = ["rent", "private"]

const ZEROED_FOR_SINGLE = [
  "child_sum",
  "child_sum_netto",
  "infant_sum",
  "infant_sum_netto",
  "child_sum_transfer",
  "child_sum_transfer_netto",
  "infant_sum_transfer",
  "infant_sum_transfer_netto",
]

const REQUIRED = [
  "name",
  "state_id",
  "company_id",
  "terminal_id",
  "transport_type_id",
  "valute_id",
  "from_date",
  "to_date",
]

export const usePriceForm = ({ urls, priceId, prefill = {} }) => {
  const { data, isLoading, isError } = useGetPriceFormDataQuery({ id: priceId, ...prefill })
  const [fetchResorts, { isFetching: isLoadingResorts }] = useLazyGetPriceResortsQuery()
  const [savePrice, { isLoading: isSaving }] = useSavePriceMutation()
  const [deletePhoto] = useDeletePricePhotoMutation()

  const [form, setForm] = useState(null)
  const [resorts, setResorts] = useState([])
  const [existingPhotos, setExistingPhotos] = useState([])
  const [newFiles, setNewFiles] = useState([])
  const [deletingPhotoId, setDeletingPhotoId] = useState(null)
  const [errors, setErrors] = useState({})
  const [serverErrors, setServerErrors] = useState([])

  useEffect(() => {
    if (!data) return
    setForm(data.price)
    setResorts(data.options.resorts)
    setExistingPhotos(data.price.photos ?? [])
  }, [data])

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev))
  }, [])

  const changeState = useCallback(
    async (stateId) => {
      setForm((prev) => ({ ...prev, state_id: stateId, region_ids: [] }))
      setErrors((prev) => ({ ...prev, state_id: undefined }))
      if (!stateId) return setResorts([])
      try {
        const res = await fetchResorts({ state_id: stateId }).unwrap()
        setResorts(res.resorts)
      } catch (_) {
        setResorts([])
      }
    },
    [fetchResorts]
  )

  const selectAllResorts = useCallback(
    () =>
      setField(
        "region_ids",
        resorts.map((r) => r.id)
      ),
    [resorts, setField]
  )
  const selectAllHotels = useCallback(
    () =>
      setField(
        "company_ids",
        (data?.options.hotel_companies ?? []).map((c) => c.id)
      ),
    [data, setField]
  )

  const addFiles = useCallback((files) => setNewFiles((prev) => [...prev, ...files]), [])
  const removeNewFile = useCallback(
    (i) => setNewFiles((prev) => prev.filter((_, idx) => idx !== i)),
    []
  )

  const removeExistingPhoto = useCallback(
    async (id) => {
      setDeletingPhotoId(id)
      try {
        await deletePhoto({ id }).unwrap()
        setExistingPhotos((prev) => prev.filter((p) => p.id !== id))
      } finally {
        setDeletingPhotoId(null)
      }
    },
    [deletePhoto]
  )

  const isSinglePrice = useMemo(
    () => SINGLE_PRICE_TYPES.includes(form?.transfer_type),
    [form?.transfer_type]
  )

  const validate = () => {
    const next = {}
    REQUIRED.forEach((k) => {
      if (!form[k]) next[k] = true
    })
    if (form.from_date && form.to_date && form.from_date > form.to_date) next.to_date = true
    if (Number(form.passenger_from) > Number(form.passenger_to)) next.passenger_to = true
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const buildFormData = () => {
    const fd = new FormData()
    const values = { ...form }
    if (isSinglePrice) ZEROED_FOR_SINGLE.forEach((k) => (values[k] = 0))

    SCALAR_FIELDS.forEach((k) => fd.append(`price[${k}]`, values[k] ?? ""))
    fd.append("price[allow_export_api]", form.allow_export_api ? "1" : "0")

    // "" гарантирует, что ключ попадёт в params даже при пустом выборе
    fd.append("regions[ids][]", "")
    form.region_ids.forEach((id) => fd.append("regions[ids][]", id))
    fd.append("companies[ids][]", "")
    form.company_ids.forEach((id) => fd.append("companies[ids][]", id))

    newFiles.forEach((f) => fd.append("price_photos[]", f))
    return fd
  }

  const submit = useCallback(async () => {
    setServerErrors([])
    if (!validate()) return
    try {
      const res = await savePrice({
        id: priceId,
        body: buildFormData(),
      }).unwrap()
      const target = res.redirect_url ?? urls.prices
      window.Turbolinks?.visit ? window.Turbolinks.visit(target) : window.location.assign(target)
    } catch (e) {
      setServerErrors(e?.data?.errors ?? ["Ошибка сохранения"])
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, newFiles, priceId, urls.prices, isSinglePrice])

  return {
    form,
    setField,
    options: data?.options,
    settings: data?.settings ?? {},
    resorts,
    isLoadingResorts,
    isLoading,
    isError,
    isSaving,
    isSinglePrice,
    errors,
    serverErrors,
    changeState,
    selectAllResorts,
    selectAllHotels,
    existingPhotos,
    newFiles,
    addFiles,
    removeNewFile,
    removeExistingPhoto,
    deletingPhotoId,
    submit,
  }
}
